import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import TierBadge from '../components/TierBadge'
import DiagnosticPanel from '../components/DiagnosticPanel'
import { pickDetail } from '../lib/data'

const PROGRAM_COLOR = {
  slate:   '#5F5E5A',
  blue:    '#378ADD',
  violet:  '#7C3AED',
  emerald: '#65A30D',
  amber:   '#D97706',
}

const STATUS_STYLE = {
  open:        { bg: '#FCEBEB', color: '#A32D2D' },
  scheduled:   { bg: '#E6F1FB', color: '#185FA5' },
  dispatched:  { bg: '#FAEEDA', color: '#854F0B' },
  remote:      { bg: '#F3E8FF', color: '#6B21A8' },
  in_progress: { bg: '#FAEEDA', color: '#854F0B' },
  blocked:     { bg: '#FCEBEB', color: '#A32D2D' },
  resolved:    { bg: '#EAF3DE', color: '#3B6D11' },
  cancelled:   { bg: '#F1EFE8', color: '#5F5E5A' },
}

const ACTIVE_STATUSES = ['open','scheduled','dispatched','remote','in_progress','blocked']

export default function SiteDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [site, setSite] = useState(null)
  const [audits, setAudits] = useState([])
  const [tickets, setTickets] = useState([])
  const [programs, setPrograms] = useState([])         // [{ program, hardware_install_status, ... }]
  const [personnel, setPersonnel] = useState([])       // [{ role, name, phone, email, ... }] for this site (GM/AGM) + district DM + region RDO
  const [loading, setLoading] = useState(true)
  const [showResolved, setShowResolved] = useState(false)

  useEffect(() => { fetchAll() }, [id])

  async function fetchAll() {
    setLoading(true)
    // Pull everything we need in one round trip — site shell, recent
    // audits with auditor names, tickets (we'll filter open/resolved
    // client-side based on the toggle), and program memberships with
    // their per-site metadata.
    const [siteRes, auditsRes, ticketsRes, programsRes] = await Promise.all([
      supabase.from('sites')
        .select('id, name, address, city, state, active, created_at, site_details(*)')
        .eq('id', id)
        .single(),
      supabase.from('audits')
        .select('id, status, notes, created_at, submitted_at, profiles:auditor_id(full_name)')
        .eq('site_id', id)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase.from('tickets')
        .select('id, ticket_number, title, tier, type, status, assignee_team, created_at, programs(slug, name, color)')
        .eq('site_id', id)
        .order('created_at', { ascending: false }),
      supabase.from('program_sites')
        .select('id, hardware_install_status, hardware_cost_cents, cutover_wave, programs(id, slug, name, color, status)')
        .eq('site_id', id),
    ])
    setSite(siteRes.data)
    setAudits(auditsRes.data || [])
    setTickets(ticketsRes.data || [])
    setPrograms(programsRes.data || [])

    // Personnel requires a second pass because the DM + RDO are scoped
    // to the site's district + region, which we only know after loading
    // site_details. Doing it sequentially adds <100ms and keeps the query
    // simple (no multi-level OR filters).
    const detForPersonnel = pickDetail(siteRes.data?.site_details)
    const districtId = detForPersonnel?.district_id
    // Three categories: site-scoped (GM/AGM), district-scoped (DM),
    // region-scoped (RDO via district -> region). We fetch all three
    // with an OR filter over site_id / district_id; the RDO lookup
    // takes an extra step because we need the region.
    const personnelRows = []
    // Site-scoped people (GM, AGM, maintenance_tech, controller, etc.)
    const { data: siteLevel } = await supabase
      .from('site_personnel')
      .select('id, role, name, phone, email, personal_email, reports_to_id, notes, active')
      .eq('site_id', id)
      .eq('active', true)
      .order('role')
    personnelRows.push(...(siteLevel || []))

    // District DM
    if (districtId) {
      const { data: dmRows } = await supabase
        .from('site_personnel')
        .select('id, role, name, phone, email, reports_to_id, active')
        .eq('district_id', districtId)
        .eq('active', true)
      personnelRows.push(...(dmRows || []))

      // Get the region for this district, then its RDO.
      const { data: distRow } = await supabase
        .from('districts').select('region_id').eq('id', districtId).maybeSingle()
      if (distRow?.region_id) {
        const { data: rdoRows } = await supabase
          .from('site_personnel')
          .select('id, role, name, phone, email, active')
          .eq('region_id', distRow.region_id)
          .eq('active', true)
        personnelRows.push(...(rdoRows || []))
      }
    }
    setPersonnel(personnelRows)

    setLoading(false)
  }

  if (loading) return <div className="spinner" />
  if (!site) return <div className="empty-state"><h3>Site not found</h3></div>

  const det = pickDetail(site.site_details)

  const visibleTickets = showResolved
    ? tickets
    : tickets.filter(t => ACTIVE_STATUSES.includes(t.status))
  const openCount = tickets.filter(t => ACTIVE_STATUSES.includes(t.status)).length
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length

  return (
    <div className="page-container">
      <button className="btn btn-sm" onClick={() => navigate('/sites')} style={{ marginBottom: 10 }}>
        ← All sites
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
              {det?.site_code && (
                <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                  {det.site_code}
                </span>
              )}
              <TierBadge tier={det?.priority_tier} rank={det?.priority_rank ?? det?.rank} />
              {!site.active && <span className="badge badge-na">Inactive</span>}
            </div>
            <h1 style={{ margin: 0, fontSize: 22 }}>{site.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              {[site.address, site.city, site.state].filter(Boolean).join(', ') || 'No address on file'}
              {det?.district && ` · ${det.district}`}
              {det?.region && det.region !== det.district && ` · ${det.region}`}
            </div>
          </div>
        </div>

        {/* Quick stats row — score+rank moved to the DiagnosticPanel below */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          <Stat label="Open tickets" value={openCount} color={openCount > 0 ? '#A32D2D' : undefined} />
          <Stat label="Audits" value={audits.length} />
          <Stat label="Programs" value={programs.length} />
        </div>

        {/* Equipment issue flags from site_details (if present) */}
        {det && (det.lpr_issue || det.gate_issue || det.loader_issue || det.tablet_internet_issue) && (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {det.lpr_issue && <IssuePill>LPR</IssuePill>}
            {det.gate_issue && <IssuePill>Gate</IssuePill>}
            {det.loader_issue && <IssuePill>Loader</IssuePill>}
            {det.tablet_internet_issue && <IssuePill>Tablet/Internet</IssuePill>}
          </div>
        )}
      </div>

      {/* Diagnostic panel — full survey + scoring breakdown */}
      <DiagnosticPanel det={det} siteId={site.id} onChange={fetchAll} />

      {/* Programs */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          Programs ({programs.length})
        </div>
        {programs.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Not linked to any program yet.
          </div>
        ) : (
          programs.map(ps => {
            const p = ps.programs
            if (!p) return null
            const stripe = PROGRAM_COLOR[p.color] || PROGRAM_COLOR.slate
            return (
              <div key={ps.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 0', cursor: 'pointer',
                  borderTop: '1px solid var(--border)',
                }}
                onClick={() => navigate(`/programs/${p.slug}`)}>
                <div style={{ width: 4, height: 32, borderRadius: 2, background: stripe, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {p.status}
                    {ps.cutover_wave && ` · Wave ${ps.cutover_wave}`}
                    {ps.hardware_cost_cents > 0 && ` · $${(ps.hardware_cost_cents / 100).toLocaleString()}`}
                  </div>
                </div>
                {ps.hardware_install_status && ps.hardware_install_status !== 'not_required' && (
                  <span className={`badge ${ps.hardware_install_status === 'complete' ? 'badge-pass' : 'badge-warn'}`}>
                    {ps.hardware_install_status.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Personnel — RDO -> DM -> GM -> AGM in that hierarchical order */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          Personnel ({personnel.length})
        </div>
        {personnel.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No personnel on file for this site yet.
          </div>
        ) : (
          sortPersonnel(personnel).map((p, i) => (
            <div key={p.id}
              style={{
                padding: '8px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
              <span style={{
                fontSize: 10, fontFamily: 'monospace',
                padding: '2px 6px', borderRadius: 10,
                background: 'var(--bg-secondary)', color: 'var(--text-muted)',
                fontWeight: 600, letterSpacing: '.03em',
                minWidth: 46, textAlign: 'center', flexShrink: 0,
              }}>
                {p.role}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>
                  {p.name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Vacant</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {p.phone && <a href={`tel:${p.phone}`} style={{ color: 'inherit' }}>{p.phone}</a>}
                  {p.email && <a href={`mailto:${p.email}`} style={{ color: 'inherit' }}>{p.email}</a>}
                  {!p.phone && !p.email && <span>No contact info</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tickets */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Tickets ({openCount} open{resolvedCount > 0 && `, ${resolvedCount} resolved`})
          </div>
          {resolvedCount > 0 && (
            <button
              className="btn btn-sm"
              onClick={() => setShowResolved(s => !s)}>
              {showResolved ? 'Hide resolved' : 'Show all'}
            </button>
          )}
        </div>
        {visibleTickets.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {openCount === 0 ? 'No open tickets — nice.' : 'No tickets to show with current filter.'}
          </div>
        ) : (
          visibleTickets.map(t => {
            const ss = STATUS_STYLE[t.status] || STATUS_STYLE.open
            return (
              <div key={t.id}
                style={{
                  padding: '8px 0', cursor: 'pointer',
                  borderTop: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onClick={() => navigate(`/tickets/${t.id}`)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                      {t.ticket_number}
                    </span>
                    <TierBadge tier={t.tier} />
                    {t.type && (
                      <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10, background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                        {t.type}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {t.assignee_team ? t.assignee_team.replace(/_/g, ' ') : 'Unassigned'}
                    {' · '}{ageDays(t.created_at)}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 10,
                  background: ss.bg, color: ss.color, fontWeight: 500,
                  textTransform: 'capitalize', flexShrink: 0,
                }}>
                  {t.status.replace(/_/g, ' ')}
                </span>
              </div>
            )
          })
        )}
      </div>

      {/* Audits */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          Recent audits ({audits.length})
        </div>
        {audits.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No audits at this site yet.
          </div>
        ) : (
          audits.map(a => (
            <div key={a.id}
              style={{
                padding: '8px 0', cursor: 'pointer',
                borderTop: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
              onClick={() => navigate(`/audits/${a.id}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {a.profiles?.full_name || 'Unknown auditor'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {a.status === 'submitted'
                    ? `Submitted ${new Date(a.submitted_at || a.created_at).toLocaleDateString()}`
                    : `Started ${new Date(a.created_at).toLocaleDateString()}`}
                </div>
              </div>
              <span className={`badge ${a.status === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
                {a.status.replace(/_/g, ' ')}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  )
}

function IssuePill({ children }) {
  return (
    <span style={{
      fontSize: 11, padding: '2px 7px', borderRadius: 10,
      background: '#FCEBEB', color: '#A32D2D', fontWeight: 500,
    }}>
      ⚠ {children}
    </span>
  )
}

function ageDays(iso) {
  if (!iso) return ''
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (d === 0) return 'Today'
  if (d === 1) return '1d ago'
  if (d < 30)  return `${d}d ago`
  if (d < 365) return `${Math.floor(d / 30)}mo ago`
  return `${Math.floor(d / 365)}y ago`
}

// Hierarchy order for personnel display. Known roles come first in this
// fixed order; unknown roles fall to the end but retain their internal
// alphabetical sort for predictability.
const PERSONNEL_ORDER = ['RDO', 'DM', 'GM', 'AGM', 'VP', 'maintenance_tech', 'controller']
function sortPersonnel(list) {
  return [...list].sort((a, b) => {
    const ai = PERSONNEL_ORDER.indexOf(a.role)
    const bi = PERSONNEL_ORDER.indexOf(b.role)
    const aRank = ai === -1 ? 999 : ai
    const bRank = bi === -1 ? 999 : bi
    if (aRank !== bRank) return aRank - bRank
    return (a.name || '').localeCompare(b.name || '')
  })
}
